import { createFileRoute, Link } from "@tanstack/react-router";

import { Footer, Nav } from "../components/lidoo/chrome";

// The methodology page: the written-down version of the editorial rules the
// catalogue already runs on. Everything stated here is checkable against the
// site itself (last-checked dates, missing prices, attribution lines), which
// is the point: it documents practice, it does not advertise intentions.
export const Route = createFileRoute("/how-we-verify")({
  head: () => ({
    meta: [
      { title: "How we verify prices and clubs | sunbedly" },
      {
        name: "description",
        content:
          "Why 8 in 10 clubs on sunbedly show no price, which sources we accept, which we refuse, and the real corrections we made along the way. The full editorial method behind the catalogue.",
      },
    ],
    links: [{ rel: "canonical", href: "https://sunbedly.com/how-we-verify" }],
  }),
  component: MethodPage,
});

function H2({ children }: { children: string }) {
  return (
    <h2 className="li-serif mt-12 text-2xl font-normal text-[#1C2430] md:text-3xl">{children}</h2>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return <p className="mt-3 max-w-[68ch] text-base leading-relaxed text-[#5B6472]">{children}</p>;
}

function MethodPage() {
  return (
    <div className="min-h-dvh bg-[#F2EFE9]">
      <Nav />
      <main className="mx-auto max-w-3xl px-5 pb-20 pt-12">
        <p className="li-mono text-[11px] font-bold uppercase tracking-[0.18em] text-[#2C4FD6]">
          Methodology
        </p>
        <h1 className="li-serif mt-2 text-4xl font-normal leading-tight text-[#1C2430] md:text-5xl">
          How we verify prices and clubs
        </h1>
        <P>
          Most of the beach club prices you find online are copied between aggregator
          sites until nobody remembers where they came from. This page describes the
          opposite approach: the rules this catalogue actually runs on, including the
          numbers we refused to publish and the mistakes we corrected in public.
        </P>

        <H2>Why 8 in 10 clubs here show no price</H2>
        <P>
          The catalogue lists 956 clubs. Only around 150 show a price, because we only
          publish a rate when the club itself publishes it: on its own website, its own
          price board, or its own booking page. A number from a review, a blog post or
          another directory does not qualify. On a site that promises real prices, a
          missing number is honest and a wrong number is poison, so the empty state is
          deliberate.
        </P>

        <H2>Real corrections we made</H2>
        <P>
          These are actual examples from building the catalogue, kept here because they
          show what the rules catch. A beach club in Saint-Tropez was listed across the
          web at €300 a day; the hotel&apos;s own rate card said €74. Two Ibiza clubs
          were shown at €6 elsewhere: that was the municipal public-beach fee for the
          neighbouring stretch of sand, not the clubs&apos; rate. One widely copied €80
          figure traced back to a single TripAdvisor review from around 2018, divided by
          two. And one club&apos;s often-quoted €25 was not even the right unit: its real
          card charges per person, in four date bands, from €85. Every one of those
          numbers was removed or corrected against the club&apos;s own published rates.
        </P>

        <H2>Where the facts come from</H2>
        <P>
          Coordinates, opening hours, ratings, review counts and facility flags come from
          the official Google Places API, refreshed within Google&apos;s permitted cache
          window and shown with attribution. Photos are uploaded by the club itself,
          served live through the Places API with the photographer credited, or taken from
          Wikimedia Commons and Openverse under a CC0, CC BY or CC BY-SA licence. Those
          last ones show the beach near the club, found by its coordinates, and are
          labelled exactly so (&quot;Beach near Vilamoura&quot;), with the author and the
          licence on the page and on{" "}
          <Link to="/photo-credits" className="underline decoration-[#2C4FD6] underline-offset-4">
            /photo-credits
          </Link>
          . We never scrape Instagram, Google Maps or review sites, and we never put a
          stock or AI-generated photo on a real, named club. Descriptions are written by
          us from the club&apos;s own published information, never copied.
        </P>

        <H2>What a listed club never pays for</H2>
        <P>
          Listing is free and commission is 0%, permanently. A club cannot pay to change
          its rating, its price display or its review scores. Paid placement, when it
          exists, is labelled as such and changes position only, never data. Reviews on
          club pages are written by visitors and can be hidden only for spam, not for
          being negative.
        </P>

        <H2>How fresh is it</H2>
        <P>
          Every club page carries a last-checked date. Prices carry the season they were
          published for. When a club closes permanently, it is removed rather than left
          to decorate the count: we have deleted clubs that other directories still list
          years after they were demolished.
        </P>

        <H2>Corrections</H2>
        <P>
          Spotted something wrong, or run a club and want your page updated? Mail{" "}
          <a href="mailto:hello@sunbedly.com" className="text-[#2C4FD6] underline underline-offset-4">
            hello@sunbedly.com
          </a>
          , or claim your page for free via{" "}
          <Link to="/partner" className="text-[#2C4FD6] underline underline-offset-4">
            the partner page
          </Link>
          . Corrections beat completeness; we would rather show less and have it be true.
        </P>

        <P>
          See the method applied at scale on{" "}
          <Link to="/sunbed-prices" className="text-[#2C4FD6] underline underline-offset-4">
            the Sunbed Price Index
          </Link>
          .
        </P>
      </main>
      <Footer />
    </div>
  );
}
